import { create } from "zustand";
import type { AuthStore, User, ApiResponse } from "../_types/auth.types";

const API_BASE = "/api";

export const useAuth = create<AuthStore>((set, get) => ({
  // State
  user: null,
  isLoading: false,
  isAuthenticated: false,
  isInitialized: false,

  // Request OTP code
  requestCode: async (email: string) => {
    set({ isLoading: true });

    try {
      const response = await fetch(`${API_BASE}/auth/request-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
        credentials: "include",
      });

      const data: ApiResponse = await response.json();

      set({ isLoading: false });

      if (!response.ok) {
        return { success: false, error: data.error || "Failed to send code" };
      }

      // In development, return the code for convenience
      const devCode = (data.data as { devCode?: string })?.devCode;
      return { success: true, devCode };
    } catch {
      set({ isLoading: false });
      return { success: false, error: "Network error. Please try again." };
    }
  },

  // Verify OTP code
  verifyCode: async (email: string, code: string) => {
    set({ isLoading: true });

    try {
      const response = await fetch(`${API_BASE}/auth/verify-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, code }),
        credentials: "include",
      });

      const data: ApiResponse<User> = await response.json();

      if (!response.ok) {
        set({ isLoading: false });
        return { success: false, error: data.error || "Invalid code" };
      }

      // Set authenticated user
      set({
        user: data.user || null,
        isAuthenticated: true,
        isLoading: false,
      });

      return { success: true };
    } catch {
      set({ isLoading: false });
      return { success: false, error: "Network error. Please try again." };
    }
  },

  // Logout
  logout: async () => {
    set({ isLoading: true });

    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }

    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  // Check current auth status
  checkAuth: async () => {
    // Don't check if already initialized and authenticated
    if (get().isInitialized && get().isAuthenticated) {
      return;
    }

    set({ isLoading: true });

    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        const data: ApiResponse = await response.json();
        set({
          user: data.user || null,
          isAuthenticated: !!data.user,
          isLoading: false,
          isInitialized: true,
        });
      } else {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true,
        });
      }
    } catch (error) {
      console.error("Auth check error:", error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
      });
    }
  },

  // Clear auth state (for manual reset)
  clearAuth: () => {
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },
}));

/**
 * Hook to get just the user
 */
export const useUser = () => useAuth((state) => state.user);

/**
 * Hook to check if authenticated
 */
export const useIsAuthenticated = () => useAuth((state) => state.isAuthenticated);

/**
 * Hook to get loading state
 */
export const useAuthLoading = () => useAuth((state) => state.isLoading);
